/**
 * ConstructionWelcome — first-time login welcome modal for /myportafolio
 *
 * Tone: transparent, friendly, ambitious. Theme: NYC night skyline with
 * cranes building neon towers. Animated SVG (no external assets, no images).
 *
 * Triggers ONCE per user (localStorage key `sim360_welcome_seen_v1`).
 * Skippable, dismissible.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { HardHat, Wrench, Sparkles, ArrowRight } from 'lucide-react';
import { detectLang } from '../utils/langState';

const KEY = 'sim360_welcome_seen_v1';

const COPY = {
  es: {
    badge: 'Espacio nuevo · en construcción',
    title: 'Bienvenido al espacio más nuevo del Club',
    subtitle: 'Estamos construyendo el futuro de la inversión personal — algunos indicadores aún se están calibrando.',
    bullet1: 'Zona del Club recién inaugurada',
    bullet2: 'Algunas métricas siguen sincronizándose',
    bullet3: 'Lo más importante: conecta tu cuenta IA y explora',
    body: 'Estamos en construcción activa — como una torre nueva en Manhattan. Tu papel ahora es ayudarnos a validar que todo funcione conectando tus cuentas MT4/5. Cada conexión nos permite afinar el sistema.',
    primary: 'Conectar mi cuenta IA',
    secondary: 'Entendido, seguir',
  },
  en: {
    badge: 'New space · under construction',
    title: "Welcome to The Club's newest space",
    subtitle: "We're building the future of personal investing — some metrics are still calibrating.",
    bullet1: 'Brand-new Club zone',
    bullet2: 'Some metrics still syncing',
    bullet3: 'Most important: connect your AI account & explore',
    body: "We're under active construction — think of it as a new tower rising in Manhattan. Your role right now is to help us validate everything end-to-end by connecting your MT4/5 accounts. Each connection lets us refine the system.",
    primary: 'Connect my AI account',
    secondary: 'Got it, continue',
  },
  fr: {
    badge: 'Nouvel espace · en construction',
    title: "Bienvenue dans le tout nouvel espace du Club",
    subtitle: "Nous bâtissons le futur de l'investissement personnel — certains indicateurs se calibrent encore.",
    bullet1: 'Zone du Club fraîchement inaugurée',
    bullet2: 'Certaines métriques continuent de synchroniser',
    bullet3: 'Le plus important : connecte ton compte IA et explore',
    body: "Construction active en cours — comme une nouvelle tour qui se dresse à Manhattan. Ton rôle maintenant : aide-nous à valider le système en connectant tes comptes MT4/5. Chaque connexion nous permet d'affiner.",
    primary: 'Connecter mon compte IA',
    secondary: 'Compris, continuer',
  },
};

/* ───────────── Animated NYC Skyline SVG ───────────── */
const CitySkyline = () => (
  <svg viewBox="0 0 600 220" className="w-full h-44" preserveAspectRatio="xMidYMax meet">
    <defs>
      <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#020817" />
        <stop offset="60%" stopColor="#0a1a3a" />
        <stop offset="100%" stopColor="#020817" />
      </linearGradient>
      <linearGradient id="tower" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#00f4ff" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#00f4ff" stopOpacity="0.05" />
      </linearGradient>
      <filter id="cityGlow">
        <feGaussianBlur stdDeviation="1.5" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    <rect width="600" height="220" fill="url(#sky)" />

    {/* Stars */}
    {Array.from({ length: 30 }, (_, i) => (
      <circle
        key={i}
        cx={(i * 73 + 17) % 600}
        cy={(i * 31 + 11) % 100}
        r={i % 5 === 0 ? 1.2 : 0.6}
        fill="white"
        opacity={0.3 + ((i * 17) % 50) / 100}
      />
    ))}

    {/* Distant buildings (silhouette) */}
    <g fill="#0e1a35" opacity="0.9">
      <rect x="0"   y="140" width="80"  height="80" />
      <rect x="78"  y="120" width="55"  height="100" />
      <rect x="130" y="155" width="40"  height="65" />
      <rect x="170" y="135" width="60"  height="85" />
      <rect x="380" y="145" width="50"  height="75" />
      <rect x="425" y="125" width="70"  height="95" />
      <rect x="490" y="160" width="45"  height="60" />
      <rect x="530" y="135" width="70"  height="85" />
    </g>

    {/* Window lights on distant buildings */}
    {Array.from({ length: 80 }, (_, i) => {
      const x = ((i * 37) % 600);
      const y = 130 + ((i * 19) % 80);
      const onDistant = (x < 240 || x > 380);
      if (!onDistant) return null;
      return (
        <rect key={`w${i}`} x={x} y={y} width="2" height="2"
          fill={i % 5 === 0 ? '#ffcc55' : '#bbeeff'} opacity={0.5 + (i % 4) * 0.1} />
      );
    })}

    {/* Center: the NEW Tower under construction */}
    <g filter="url(#cityGlow)">
      {/* Tower shaft with growing floors */}
      <rect x="265" y="60" width="70" height="160" fill="url(#tower)" stroke="#00f4ff" strokeWidth="1" />
      {/* Construction floors (incomplete top) */}
      <rect x="265" y="60" width="70" height="14" fill="none" stroke="#ff3030" strokeWidth="1" strokeDasharray="4 3" />
      <rect x="265" y="74" width="70" height="14" fill="none" stroke="#ff8c00" strokeWidth="1" strokeDasharray="4 3" />
      <rect x="265" y="88" width="70" height="14" fill="none" stroke="#22c55e" strokeWidth="1" strokeDasharray="4 3" />
      {/* Solid lower floors */}
      {Array.from({ length: 8 }, (_, i) => (
        <line key={i} x1="265" y1={102 + i * 14} x2="335" y2={102 + i * 14}
          stroke="#00f4ff" strokeWidth="0.5" opacity="0.4" />
      ))}
      {/* Tower windows */}
      {Array.from({ length: 24 }, (_, i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        return (
          <rect key={`tw${i}`} x={272 + col * 16} y={108 + row * 16}
            width="6" height="8" fill="#00f4ff"
            opacity={0.4 + ((i * 7) % 6) * 0.1} />
        );
      })}
    </g>

    {/* Crane on top */}
    <g stroke="#ff8c00" strokeWidth="1.5" fill="none" strokeLinecap="round">
      {/* Vertical mast */}
      <line x1="300" y1="60" x2="300" y2="30" />
      {/* Horizontal jib */}
      <line x1="240" y1="35" x2="360" y2="35" />
      {/* Diagonal braces */}
      <line x1="300" y1="30" x2="245" y2="35" />
      <line x1="300" y1="30" x2="355" y2="35" />
      {/* Counter-weight */}
      <rect x="240" y="32" width="8" height="6" fill="#ff8c00" stroke="none" />
      {/* Hook cable + load */}
      <line x1="320" y1="35" x2="320" y2="65" />
      <rect x="316" y="65" width="8" height="6" fill="#ff8c00" stroke="none" />
    </g>
    {/* Blinking aviation light */}
    <circle cx="300" cy="30" r="2.2" fill="#ff3030" style={{ animation: 'sim360-blink 1.4s ease-in-out infinite' }} />

    {/* Foreground silhouette */}
    <path d="M0 220 L600 220 L600 200 L520 200 L520 195 L500 195 L500 200 L0 200 Z" fill="#020817" />
  </svg>
);

const ConstructionWelcome = ({ onConnect }) => {
  const [open, setOpen] = useState(false);
  const [lang] = useState(() => detectLang());

  useEffect(() => {
    let seen = true;
    try { seen = !!localStorage.getItem(KEY); } catch (_) { /* localStorage blocked */ }
    if (seen) return undefined;
    // small delay to feel natural after the dashboard renders
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try { localStorage.setItem(KEY, '1'); } catch (_) { /* localStorage blocked */ }
    setOpen(false);
  };
  const goConnect = () => {
    close();
    if (typeof onConnect === 'function') onConnect();
    else {
      // Scroll to the MT4/5 connect section if id exists
      const el = document.querySelector('[data-testid="mt-module"]') || document.getElementById('mt-module');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const c = COPY[lang] || COPY.es;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{
        background: 'rgba(2,8,23,0.85)',
        backdropFilter: 'blur(8px)',
        animation: 'sim360-fade-in 0.25s ease-out',
      }}
      onClick={close}
      data-testid="construction-welcome-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #04102a 0%, #020817 100%)',
          border: '1px solid rgba(0,244,255,0.35)',
          boxShadow: '0 25px 80px -20px rgba(0,244,255,0.45)',
          animation: 'sim360-modal-in 0.32s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
        data-testid="construction-welcome-modal"
      >
        {/* Skyline header */}
        <div className="relative">
          <CitySkyline />
          <div className="absolute top-3 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(255,140,0,0.12)', border: '1px solid rgba(255,140,0,0.45)' }}>
            <HardHat className="w-3 h-3 text-[#ff8c00]" />
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#ff8c00]">{c.badge}</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <h2 className="text-2xl font-black text-white leading-tight mb-2">{c.title}</h2>
          <p className="text-[#9EB4C0] text-sm mb-4">{c.subtitle}</p>

          <p className="text-[#cfd9e3] text-[13px] leading-relaxed mb-4">{c.body}</p>

          <ul className="space-y-2 mb-5 text-[13px] text-white">
            <li className="flex items-start gap-2">
              <HardHat className="w-4 h-4 mt-0.5 text-[#ff8c00] shrink-0" />
              <span>{c.bullet1}</span>
            </li>
            <li className="flex items-start gap-2">
              <Wrench className="w-4 h-4 mt-0.5 text-[#00f4ff] shrink-0" />
              <span>{c.bullet2}</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 text-[#22c55e] shrink-0" />
              <span>{c.bullet3}</span>
            </li>
          </ul>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={goConnect}
              data-testid="welcome-cta-connect"
              className="flex-1 bg-gradient-to-r from-[#0051ff] to-[#00e5ff] text-[#020817] font-bold h-11 rounded-full hover:brightness-110"
            >
              {c.primary} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              onClick={close}
              variant="outline"
              data-testid="welcome-cta-dismiss"
              className="border-white/15 text-white hover:bg-white/5 h-11 rounded-full"
            >
              {c.secondary}
            </Button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes sim360-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sim360-modal-in {
          from { opacity: 0; transform: translateY(30px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes sim360-blink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.2; }
        }
      `}</style>
    </div>
  );
};

export default ConstructionWelcome;
